import type { VirtualElement } from '@popperjs/core'
import { Popper, type PopperProps } from '../primitives/popper.js'
import React, { useCallback } from 'react'
import { useDelayedValue } from '../hooks/delayed-value.js'
import { ClickAwayListener } from '../primitives/click-away-listener.js'
import { Fade } from '../primitives/fade.js'

export interface PopupProps extends Omit<PopperProps, 'children'> {
  open: boolean
  /** Delay showing the popup for the specified number of milliseconds */
  delay?: number
  anchorEl: HTMLElement | VirtualElement | null
  clickAway?: boolean
  closeOnEscape?: boolean
  closeOnBackspace?: boolean
  children: React.ReactNode
  onClose?(event: Event): void
}

export const Popup: React.FC<PopupProps> = props => {
  const {
    open: requestedOpen,
    anchorEl,
    onClose,
    children,
    closeOnBackspace = false,
    closeOnEscape = true,
    clickAway = true,
    delay = 0,
    ...restProps
  } = props

  const open = useDelayedValue(requestedOpen, delay)

  const handleKeyDown = useCallback(
    (ev: React.KeyboardEvent) => {
      if (!onClose) {
        return
      }
      if ((closeOnEscape && ev.key === 'Escape') || (closeOnBackspace && ev.key === 'Backspace')) {
        ev.preventDefault()
        onClose(ev.nativeEvent)
      }
    },
    [closeOnBackspace, closeOnEscape, onClose]
  )

  if (!open) {
    return null
  }
  const popper = (
    <Popper
      {...restProps}
      contentEditable={false}
      open={open}
      transition
      disablePortal={false}
      keepMounted={false}
      placement="bottom-start"
      anchorEl={anchorEl}
      onKeyDown={handleKeyDown}
      style={{
        zIndex: 1300,
        maxHeight: '50%',
        overflow: 'auto',
      }}
    >
      {({ TransitionProps }: { TransitionProps?: { in?: boolean; onEnter?: () => void; onExited?: () => void } }) => (
        <Fade {...TransitionProps}>
          <div>{children}</div>
        </Fade>
      )}
    </Popper>
  )

  return clickAway && onClose ? <ClickAwayListener onClickAway={onClose}>{popper}</ClickAwayListener> : popper
}

export default Popup
